import React, { useState } from 'react'
import {ArrowLeft, X, Plus, Trash2, Download} from 'lucide-react'

interface BatchRow {
  id: number
  arena: string
  limo: string
  arcilla: string
  textura: string
  error: string
}

interface SoilTextureCalculatorProps {
  onBack: () => void
}

const SoilTextureCalculator: React.FC<SoilTextureCalculatorProps> = ({ onBack }) => {
  const [arena, setArena] = useState('')
  const [limo, setLimo] = useState('')
  const [arcilla, setArcilla] = useState('')
  const [resultado, setResultado] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const clasificarTextura = (arenaVal: number, limoVal: number, arcillaVal: number): string => {
    const total = arenaVal + limoVal + arcillaVal
    if (total > 101 || total < 99) {
      throw new Error('La suma de Arena, Limo y Arcilla debe ser aproximadamente 100% (entre 99% y 101%)')
    }

    if (arenaVal >= 0 && arenaVal <= 45 && limoVal >= 0 && limoVal <= 40 && arcillaVal >= 40 && arcillaVal <= 100) {
      return "Arcillosa"
    } else if (arenaVal >= 45 && arenaVal < 65 && limoVal >= 0 && limoVal < 20 && arcillaVal >= 35 && arcillaVal < 55) {
      return "Arcillosa Arenosa"
    } else if (arenaVal >= 20 && arenaVal < 45 && limoVal >= 15 && limoVal < 52 && arcillaVal >= 27 && arcillaVal < 40) {
      return "Franco Arcillosa"
    } else if (arenaVal >= 0 && arenaVal <= 20 && limoVal >= 40 && limoVal < 60 && arcillaVal >= 40 && arcillaVal < 60) {
      return "Arcillosa Limosa"
    } else if (arenaVal >= 0 && arenaVal <= 20 && limoVal >= 40 && limoVal <= 73 && arcillaVal >= 27 && arcillaVal <= 40) {
      return "Franco Arcillosa Limosa"
    } else if (arenaVal >= 45 && arenaVal <= 80 && limoVal >= 0 && limoVal <= 28 && arcillaVal >= 20 && arcillaVal <= 35) {
      return "Franco Arcillosa Arenosa"
    } else if (arenaVal >= 23 && arenaVal < 52 && limoVal >= 28 && limoVal < 50 && arcillaVal >= 7 && arcillaVal < 27) {
      return "Franco"
    } else if (arenaVal >= 0 && arenaVal < 50 && limoVal >= 50 && limoVal < 100 && arcillaVal >= 0 && arcillaVal < 27) {
      if (arenaVal >= 0 && arenaVal < 20 && limoVal >= 80 && limoVal <= 100 && arcillaVal >= 0 && arcillaVal < 12) {
        return "Limosa"
      } else {
        return "Franco Limosa"
      }
    } else if (
      (arenaVal >= 42 && arenaVal < 52 && limoVal >= 42 && limoVal <= 50 && arcillaVal >= 0 && arcillaVal < 7) ||
      (arenaVal >= 52 && arenaVal <= 70 && limoVal >= 10 && limoVal < 50 && arcillaVal >= 0 && arcillaVal < 20) ||
      (arenaVal >= 70 && arenaVal <= 80 && limoVal >= 0 && limoVal < 20 && arcillaVal >= 10 && arcillaVal < 20)
    ) {
      return "Franco Arenosa"
    } else if (arenaVal >= 70 && arenaVal <= 80 && limoVal >= 10 && limoVal <= 30 && arcillaVal >= 0 && arcillaVal <= 10) {
      if (Math.abs(80 - arenaVal) + arcillaVal >= 10) {
        return "Franco Arenosa"
      } else {
        return "Arenosa Franco"
      }
    } else if (arenaVal >= 80 && arenaVal < 90 && limoVal >= 0 && limoVal < 10 && arcillaVal >= 10 && arcillaVal < 20) {
      if (Math.abs(90 - arenaVal) + Math.abs(10 - arcillaVal) >= 10) {
        return "Franco Arenosa"
      } else {
        return "Arenosa Franco"
      }
    } else if (arenaVal >= 80 && arenaVal < 85 && limoVal >= 0 && limoVal < 20 && arcillaVal >= 0 && arcillaVal < 10) {
      return "Arenosa Franco"
    } else if (arenaVal >= 90 && arenaVal <= 100 && limoVal >= 0 && limoVal < 10 && arcillaVal >= 0 && arcillaVal <= 10) {
      return "Arenosa"
    } else if (arenaVal >= 85 && arenaVal <= 90 && limoVal >= 0 && limoVal < 15 && arcillaVal >= 0 && arcillaVal <= 10) {
      if ((Math.abs(90 - arenaVal) * 2 + arcillaVal) >= 10) {
        return "Arenosa Franco"
      } else {
        return "Arenosa"
      }
    } else {
      return "Error - No se pudo determinar la textura"
    }
  }

  const calcularTextura = () => {
    setError(null)
    setResultado(null)

    if (!arena || !limo || !arcilla) {
      setError('Por favor complete todos los campos')
      return
    }

    const arenaVal = parseFloat(arena)
    const limoVal = parseFloat(limo)
    const arcillaVal = parseFloat(arcilla)

    if (isNaN(arenaVal) || isNaN(limoVal) || isNaN(arcillaVal)) {
      setError('Por favor ingrese valores numéricos válidos')
      return
    }

    if (arenaVal < 0 || arenaVal > 100 || limoVal < 0 || limoVal > 100 || arcillaVal < 0 || arcillaVal > 100) {
      setError('Los valores deben estar entre 0 y 100%')
      return
    }

    try {
      const textura = clasificarTextura(arenaVal, limoVal, arcillaVal)
      setResultado(textura)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error en el cálculo')
    }
  }

  // --- Batch state ---
  const [batchRows, setBatchRows] = useState<BatchRow[]>(() =>
    Array.from({ length: 3 }, (_, i) => ({ id: i + 1, arena: '', limo: '', arcilla: '', textura: '', error: '' }))
  )
  const [nextId, setNextId] = useState(4)

  const calcBatchTextura = (a: string, l: string, c: string): { textura: string; error: string } => {
    if (!a || !l || !c) return { textura: '', error: '' }
    const av = parseFloat(a), lv = parseFloat(l), cv = parseFloat(c)
    if (isNaN(av) || isNaN(lv) || isNaN(cv)) return { textura: '', error: 'Valor inválido' }
    try {
      const t = clasificarTextura(av, lv, cv)
      return { textura: t, error: '' }
    } catch {
      return { textura: '', error: 'Suma ≠ 100%' }
    }
  }

  const updateBatchRow = (id: number, field: 'arena' | 'limo' | 'arcilla', value: string) => {
    setBatchRows(prev => prev.map(r => {
      if (r.id !== id) return r
      const updated = { ...r, [field]: value }
      const { textura, error } = calcBatchTextura(
        field === 'arena' ? value : updated.arena,
        field === 'limo' ? value : updated.limo,
        field === 'arcilla' ? value : updated.arcilla
      )
      return { ...updated, textura, error }
    }))
  }

  const addBatchRow = () => {
    setBatchRows(prev => [...prev, { id: nextId, arena: '', limo: '', arcilla: '', textura: '', error: '' }])
    setNextId(n => n + 1)
  }

  const [pasteInput, setPasteInput] = useState('')
  const [pasteError, setPasteError] = useState('')

  const handlePasteImport = () => {
    setPasteError('')
    const lines = pasteInput
      .split(/\n/)
      .map(l => l.trim())
      .filter(l => l.length > 0)

    if (lines.length === 0) {
      setPasteError('No se encontraron datos para importar.')
      return
    }

    const newRows: BatchRow[] = []
    let localId = nextId
    let hasError = false

    for (const line of lines) {
      const parts = line.split(/[,;\t]/).map(p => p.trim())
      if (parts.length < 3) {
        setPasteError(`Formato incorrecto en: "${line}" — usa arena,limo,arcilla`)
        hasError = true
        break
      }
      const [a, l, c] = parts
      const { textura, error } = calcBatchTextura(a, l, c)
      newRows.push({ id: localId++, arena: a, limo: l, arcilla: c, textura, error })
    }

    if (!hasError && newRows.length > 0) {
      setBatchRows(prev => {
        const filtered = prev.filter(r => r.arena !== '' || r.limo !== '' || r.arcilla !== '')
        return [...filtered, ...newRows]
      })
      setNextId(localId)
      setPasteInput('')
    }
  }

  const removeBatchRow = (id: number) => {
    setBatchRows(prev => prev.length > 1 ? prev.filter(r => r.id !== id) : prev)
  }

  const limpiar = () => {
    setArena('')
    setLimo('')
    setArcilla('')
    setResultado(null)
    setError(null)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Volver a Herramientas</span>
          </button>
          <div className="text-center">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">
              Calculadora de Textura de Suelo
            </h1>
            <p className="text-lg text-slate-600">
              Clasificación según el sistema del USDA (Departamento de Agricultura de los Estados Unidos)
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <X className="h-5 w-5 text-red-400 flex-shrink-0" />
                <p className="ml-3 text-sm text-red-700">{error}</p>
              </div>
              <button onClick={() => setError(null)} className="ml-3 text-red-400 hover:text-red-500">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Calculator */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-slate-900">
              Ingrese el porcentaje de cada componente
            </h2>
            <button
              onClick={limpiar}
              className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Limpiar
            </button>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-100">
            <p className="text-sm text-slate-700">
              <strong>Nota:</strong> La suma de Arena, Limo y Arcilla debe ser igual a 100%
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Arena (%)</label>
              <input
                type="number" step="0.1" min="0" max="100"
                value={arena}
                onChange={(e) => setArena(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                placeholder="0 - 100"
              />
              <p className="text-xs text-slate-500 mt-1">Partículas 0.05 – 2.0 mm</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Limo (%)</label>
              <input
                type="number" step="0.1" min="0" max="100"
                value={limo}
                onChange={(e) => setLimo(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                placeholder="0 - 100"
              />
              <p className="text-xs text-slate-500 mt-1">Partículas 0.002 – 0.05 mm</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Arcilla (%)</label>
              <input
                type="number" step="0.1" min="0" max="100"
                value={arcilla}
                onChange={(e) => setArcilla(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                placeholder="0 - 100"
              />
              <p className="text-xs text-slate-500 mt-1">Partículas menores a 0.002 mm</p>
            </div>
          </div>

          <button
            onClick={calcularTextura}
            className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-blue-500 to-green-500 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-green-600 transition-all shadow-md hover:shadow-lg"
          >
            Calcular Textura
          </button>

          {resultado && (
            <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 rounded-lg">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Clase Textural:</h3>
              <p className="text-4xl font-bold text-blue-600 mb-4">{resultado}</p>
              <div className="text-sm text-slate-600 space-y-1">
                <p><strong>Arena:</strong> {arena}%</p>
                <p><strong>Limo:</strong> {limo}%</p>
                <p><strong>Arcilla:</strong> {arcilla}%</p>
              </div>
            </div>
          )}
        </div>

        {/* Batch table */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-8">
          <div className="flex justify-between items-center mb-2">
            <div>
              <h3 className="text-xl font-bold text-slate-900">Análisis en Lote</h3>
              <p className="text-sm text-slate-500 mt-0.5">Ingrese múltiples muestras; la textura se calcula automáticamente.</p>
            </div>
            <button
              onClick={() => {
                const header = 'Muestra,Arena (%),Limo (%),Arcilla (%),Textura USDA\n'
                const body = batchRows.map((r, i) =>
                  `${i + 1},${r.arena},${r.limo},${r.arcilla},${r.textura || r.error}`
                ).join('\n')
                const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8;' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = 'soil_texture_HHTools.csv'
                a.click()
                URL.revokeObjectURL(url)
              }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors shadow"
            >
              <Download className="w-4 h-4" />
              Descargar CSV
            </button>
          </div>

          {/* Quick paste area */}
          <div className="mt-4 mb-5 p-4 bg-slate-50 border border-dashed border-slate-300 rounded-xl">
            <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Pegado Rápido</p>
            <p className="text-xs text-slate-500 mb-3">
              Pegue una o más filas en formato <code className="bg-slate-200 px-1 rounded font-mono">arena,limo,arcilla</code> — una por línea. Ej.: <code className="bg-slate-200 px-1 rounded font-mono">23,23,60</code>
            </p>
            <div className="flex gap-2 items-start">
              <textarea
                value={pasteInput}
                onChange={e => { setPasteInput(e.target.value); setPasteError('') }}
                rows={3}
                placeholder={"23,23,60\n45,30,25\n10,50,40"}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-400 focus:border-blue-400 resize-none"
              />
              <button
                onClick={handlePasteImport}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow whitespace-nowrap"
              >
                Importar
              </button>
            </div>
            {pasteError && (
              <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
                <X className="w-3 h-3" />{pasteError}
              </p>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto mt-4 max-h-96 overflow-y-auto rounded-lg border border-slate-100">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-100 text-slate-700">
                  <th className="px-3 py-2 text-left font-semibold rounded-tl-lg w-10">#</th>
                  <th className="px-3 py-2 text-left font-semibold">Arena (%)</th>
                  <th className="px-3 py-2 text-left font-semibold">Limo (%)</th>
                  <th className="px-3 py-2 text-left font-semibold">Arcilla (%)</th>
                  <th className="px-3 py-2 text-left font-semibold">Textura USDA</th>
                  <th className="px-3 py-2 rounded-tr-lg w-10"></th>
                </tr>
              </thead>
              <tbody>
                {batchRows.map((row, idx) => (
                  <tr key={row.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="px-3 py-1.5 text-slate-400 font-mono text-xs">{idx + 1}</td>
                    <td className="px-2 py-1">
                      <input
                        type="number" step="0.1" min="0" max="100"
                        value={row.arena}
                        onChange={(e) => updateBatchRow(row.id, 'arena', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-200 rounded focus:ring-1 focus:ring-blue-400 focus:border-blue-400 text-sm"
                        placeholder="0-100"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="number" step="0.1" min="0" max="100"
                        value={row.limo}
                        onChange={(e) => updateBatchRow(row.id, 'limo', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-200 rounded focus:ring-1 focus:ring-blue-400 focus:border-blue-400 text-sm"
                        placeholder="0-100"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="number" step="0.1" min="0" max="100"
                        value={row.arcilla}
                        onChange={(e) => updateBatchRow(row.id, 'arcilla', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-200 rounded focus:ring-1 focus:ring-blue-400 focus:border-blue-400 text-sm"
                        placeholder="0-100"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      {row.textura ? (
                        <span className="inline-block px-2 py-0.5 bg-green-100 text-green-800 rounded font-medium text-xs">{row.textura}</span>
                      ) : row.error ? (
                        <span className="inline-block px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">{row.error}</span>
                      ) : (
                        <span className="text-slate-300 text-xs italic">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-center">
                      <button
                        onClick={() => removeBatchRow(row.id)}
                        className="text-red-400 hover:text-red-600 transition-colors"
                        title="Eliminar fila"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            onClick={addBatchRow}
            className="mt-4 flex items-center gap-2 px-4 py-2 border-2 border-dashed border-blue-300 text-blue-500 hover:border-blue-400 hover:text-blue-600 rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Agregar fila
          </button>
        </div>

        {/* Additional info */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-8">
          <h3 className="text-xl font-bold text-slate-900 mb-4">Clases Texturales del USDA</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-900 mb-2">Texturas Gruesas</h4>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• Arenosa</li>
                <li>• Franco Arenosa</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-900 mb-2">Texturas Medias</h4>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• Franco</li>
                <li>• Franco Limosa</li>
                <li>• Limosa</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-900 mb-2">Texturas Moderadamente Finas</h4>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• Franco Arcillosa</li>
                <li>• Franco Arcillosa Arenosa</li>
                <li>• Franco Arcillosa Limosa</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-900 mb-2">Texturas Finas</h4>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• Arcillosa</li>
                <li>• Arcillosa Arenosa</li>
                <li>• Arcillosa Limosa</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-slate-700">
            <strong>Nota:</strong> Esta calculadora utiliza el sistema de clasificación textural del USDA.
            Los resultados son estimaciones basadas en los porcentajes ingresados. Para un análisis preciso,
            se recomienda realizar ensayos de laboratorio certificados.
          </p>
        </div>
      </main>
    </div>
  )
}

export default SoilTextureCalculator
